/**
 * Swiper accessibility helpers for Shopify (global scripts, no bundler).
 *
 * For aria-hidden / tabindex on off-screen slides, Swiper should use
 * watchSlidesProgress: true so slides get .swiper-slide-fully-visible.
 */
(function (window) {
  'use strict';

  var FOCUSABLE_SELECTOR =
    'button, [href], input, select, textarea, [tabindex="0"], iframe, object, embed, [tabindex]:not([tabindex="-1"])';

  /**
   * @param {Element} element
   * @returns {NodeListOf<Element>}
   */
  function getAllFocusableElement(element) {
    if (!element || typeof element.querySelectorAll !== 'function') {
      return [];
    }
    return element.querySelectorAll(FOCUSABLE_SELECTOR);
  }

  /**
   * @param {Object} swiper - Swiper instance
   */
  function normalizeNavEl(el) {
    if (!el) return [];
    if (el.length !== undefined && typeof el.setAttribute !== 'function') {
      return Array.prototype.slice.call(el).filter(function (node) {
        return node && typeof node.setAttribute === 'function';
      });
    }
    return typeof el.setAttribute === 'function' ? [el] : [];
  }

  function setNavArrowState(arrow) {
    if (!arrow || typeof arrow.setAttribute !== 'function') return;
    var isDisabled = arrow.classList && arrow.classList.contains('swiper-button-disabled');
    arrow.setAttribute('tabindex', isDisabled ? '-1' : '0');
    arrow.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
  }

  function swiperAccessibleButtonCb(swiper) {
    var prevArrows = normalizeNavEl(swiper && swiper.navigation && swiper.navigation.prevEl);
    var nextArrows = normalizeNavEl(swiper && swiper.navigation && swiper.navigation.nextEl);
    prevArrows.forEach(setNavArrowState);
    nextArrows.forEach(setNavArrowState);
  }

  /**
   * @param {Object} swiper - Swiper instance
   * @param {string} [className] - Active / visible slide class
   */
  function swiperSlidesAccessibleCb(swiper, className) {
    var slides = swiper && swiper.slides;
    var activeClass = className || 'swiper-slide-fully-visible';
    if (!slides || !slides.length) return;

    for (var i = 0; i < slides.length; i++) {
      var slide = slides[i];
      if (!slide || !slide.classList) continue;

      var isVisible = slide.classList.contains(activeClass);
      var focusables = getAllFocusableElement(slide);

      if (isVisible) {
        slide.setAttribute('aria-hidden', 'false');
        for (var j = 0; j < focusables.length; j++) {
          focusables[j].setAttribute('tabindex', '0');
          focusables[j].setAttribute('aria-hidden', 'false');
        }
      } else {
        slide.setAttribute('aria-hidden', 'true');
        for (var k = 0; k < focusables.length; k++) {
          focusables[k].setAttribute('tabindex', '-1');
          focusables[k].setAttribute('aria-hidden', 'true');
        }
      }
    }
  }

  /**
   * @param {Object} swiper - Swiper instance
   * @param {string} [className]
   * @param {number} [delayMs]
   */
  function sliderCallbackADA(swiper, className, delayMs) {
    var ms = delayMs === undefined ? 500 : delayMs;
    window.setTimeout(function () {
      swiperAccessibleButtonCb(swiper);
      swiperSlidesAccessibleCb(swiper, className);
    }, ms);
  }

  /**
   * Bind ADA updates to Swiper lifecycle (call after `new Swiper(...)`, or
   * if the instance is already initialized it runs once immediately).
   *
   * @param {Object} swiper - Swiper instance
   * @param {Object} [options]
   * @param {string} [options.activeSlideClass] - default swiper-slide-fully-visible
   * @param {number} [options.callbackDelayMs] - default 500
   * @returns {Object|undefined} swiper
   */
  function initAccessibleSwiper(swiper, options) {
    if (!swiper || typeof swiper.on !== 'function') return;
    if (swiper.__customAdaSwiperBound) return swiper;
    swiper.__customAdaSwiperBound = true;

    options = options || {};
    var className = options.activeSlideClass || 'swiper-slide-fully-visible';
    var delayMs = options.callbackDelayMs;

    function run() {
      sliderCallbackADA(swiper, className, delayMs);
    }

    var events = [
      'init',
      'slideChange',
      'beforeSlideChangeStart',
      'resize',
      'observerUpdate',
      'slidesLengthChange',
      'toEdge',
      'fromEdge',
      'reachBeginning',
      'reachEnd',
      'navigationPrev',
      'navigationNext',
      'lock',
      'unlock',
    ];
    for (var e = 0; e < events.length; e++) {
      swiper.on(events[e], run);
    }

    if (swiper.initialized) {
      run();
    }
    return swiper;
  }

  /**
   * Merge `on` handlers into a Swiper config so ADA runs with your callbacks.
   *
   * @param {Object} config - Swiper constructor options
   * @param {Object} [adaOptions]
   * @param {string} [adaOptions.activeSlideClass]
   * @param {number} [adaOptions.callbackDelayMs]
   * @returns {Object} merged config (shallow copy + merged `on`)
   */
  function mergeSwiperConfigWithADA(config, adaOptions) {
    config = config || {};
    adaOptions = adaOptions || {};
    var className = adaOptions.activeSlideClass || 'swiper-slide-fully-visible';
    var delayMs = adaOptions.callbackDelayMs;

    var out = {};
    var k;
    for (k in config) {
      if (Object.prototype.hasOwnProperty.call(config, k) && k !== 'on') {
        out[k] = config[k];
      }
    }

    var existingOn = config.on || {};
    var mergedOn = {};
    for (k in existingOn) {
      if (Object.prototype.hasOwnProperty.call(existingOn, k)) {
        mergedOn[k] = existingOn[k];
      }
    }

    function wrapEvent(eventName) {
      var prev = mergedOn[eventName];
      mergedOn[eventName] = function (swiper) {
        if (typeof prev === 'function') {
          prev.call(this, swiper);
        }
        sliderCallbackADA(swiper, className, delayMs);
      };
    }

    wrapEvent('init');
    wrapEvent('slideChange');
    wrapEvent('beforeSlideChangeStart');
    wrapEvent('resize');
    wrapEvent('observerUpdate');

    out.on = mergedOn;
    return out;
  }

  window.CustomAdaSwiper = {
    getAllFocusableElement: getAllFocusableElement,
    swiperAccessibleButtonCb: swiperAccessibleButtonCb,
    swiperSlidesAccessibleCb: swiperSlidesAccessibleCb,
    sliderCallbackADA: sliderCallbackADA,
    initAccessibleSwiper: initAccessibleSwiper,
    mergeSwiperConfigWithADA: mergeSwiperConfigWithADA,
  };

  /**
   * Resolve the per-product title to use for the wishlist button aria-label.
   *
   * @param {HTMLElement} btn
   * @returns {string}
   */
  function getWishlistButtonTitle(btn) {
    return (
      btn.dataset.productTitle ||
      btn.closest('.product-card')?.querySelector('.product-card__title')?.textContent?.trim() ||
      btn
        .closest('.product-section')
        ?.querySelector('.product-section__title')
        ?.textContent?.trim() ||
      ''
    );
  }

  /**
   * Apply aria-pressed and aria-label to a single wishlist button based on
   * whether the underlying product/variant is currently in the wishlist.
   *
   * @param {HTMLElement} btn
   * @param {boolean} isInList
   */
  function applyWishlistButtonState(btn, isInList) {
    btn.setAttribute('aria-pressed', isInList ? 'true' : 'false');

    var title = getWishlistButtonTitle(btn);
    if (title) {
      btn.setAttribute(
        'aria-label',
        (isInList ? 'Remove ' : 'Add ') + title + (isInList ? ' from wishlist' : ' to wishlist'),
      );
    }
  }

  /**
   * Swym overwrites wishlist button aria-label with a generic "Add to wishlist".
   * Re-apply per-product labels using the current aria-pressed state as the
   * source of truth (set by syncWishlistPressedState when Swym is ready).
   *
   * @param {Document|Element} [root]
   */
  function updateWishlistAriaLabels(root) {
    var scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    var buttons = scope.querySelectorAll('.swym-button.product-section__wishlist-btn');

    buttons.forEach(function (btn) {
      var isInList = btn.getAttribute('aria-pressed') === 'true';
      applyWishlistButtonState(btn, isInList);
    });
  }

  function scheduleWishlistAriaLabelUpdate(root) {
    updateWishlistAriaLabels(root);
    setTimeout(function () {
      updateWishlistAriaLabels(root);
    }, 0);
    setTimeout(function () {
      updateWishlistAriaLabels(root);
    }, 500);
  }

  function initWishlistAriaLabels() {
    scheduleWishlistAriaLabelUpdate(document);

    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(function () {
      scheduleWishlistAriaLabelUpdate(document);
    });

    document.addEventListener('liquid-ajax-cart:request-end', function () {
      scheduleWishlistAriaLabelUpdate(document);
    });
    document.addEventListener('liquid-ajax-cart:queue-end', function () {
      scheduleWishlistAriaLabelUpdate(document);
    });
  }

  // ====================================================================
  // Wishlist aria-pressed sync (Swym state)
  // ====================================================================
  //
  //   Swym tracks wishlist membership client-side. Mirror that state onto
  //   each .swym-button.product-section__wishlist-btn so screen readers
  //   announce the correct toggle state ("pressed" when in the wishlist).
  //
  //   Buttons render with data-with-epi='true', so Swym matches on epi
  //   (variant id). We also fall back to empi (product id) for safety.

  /**
   * Build a Set of string ids from a Swym wishlist items array.
   *
   * @param {Array<{epi?: (number|string), empi?: (number|string)}>} items
   * @returns {Set<string>}
   */
  function buildWishlistIdSet(items) {
    var ids = new Set();
    (items || []).forEach(function (item) {
      if (!item) return;
      if (item.epi != null) ids.add(String(item.epi));
      if (item.empi != null) ids.add(String(item.empi));
    });
    return ids;
  }

  /**
   * @param {HTMLElement} btn
   * @param {Set<string>} idSet
   * @returns {boolean}
   */
  function isWishlistButtonInList(btn, idSet) {
    var variantId = String(btn.dataset.variantId || '');
    var productId = String(btn.dataset.productId || '');
    return (variantId && idSet.has(variantId)) || (productId && idSet.has(productId));
  }

  /**
   * Refresh aria-pressed (and the matching aria-label) on every wishlist
   * button in `root` based on the current Swym wishlist items.
   *
   * @param {Array} items
   * @param {Document|Element} [root]
   */
  function syncWishlistPressedState(items, root) {
    var scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    var idSet = buildWishlistIdSet(items);

    scope.querySelectorAll('.swym-button.product-section__wishlist-btn').forEach(function (btn) {
      applyWishlistButtonState(btn, isWishlistButtonInList(btn, idSet));
    });
  }

  /**
   * Wire Swym callbacks so aria-pressed stays in sync on initial load,
   * after add/remove, and after AJAX cart events (which can re-render cards).
   */
  function initWishlistPressedSync() {
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(function (swat) {
      if (!swat) return;

      var lastItems = [];

      function fetchAndSync() {
        try {
          swat.fetch(swat.evts.fetchList, function (items) {
            lastItems = items || [];
            syncWishlistPressedState(lastItems);
          });
        } catch (e) {
          /* swat.fetch may throw if Swym isn't fully ready; ignore */
        }
      }

      fetchAndSync();

      try {
        if (swat.evtLayer && swat.JSEvents) {
          if (swat.JSEvents.addedToWishlist) {
            swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, fetchAndSync);
          }
          if (swat.JSEvents.removedFromWishlist) {
            swat.evtLayer.addEventListener(swat.JSEvents.removedFromWishlist, fetchAndSync);
          }
        }
      } catch (e) {
        /* Older Swym builds may not expose evtLayer; degrade silently */
      }

      // Re-apply state after AJAX cart updates (cart drawer can re-render
      // recommendation cards that include the wishlist button).
      function reapply() {
        syncWishlistPressedState(lastItems);
      }
      document.addEventListener('liquid-ajax-cart:request-end', reapply);
      document.addEventListener('liquid-ajax-cart:queue-end', reapply);
    });
  }

  window.updateWishlistAriaLabels = updateWishlistAriaLabels;
  window.scheduleWishlistAriaLabelUpdate = scheduleWishlistAriaLabelUpdate;
  window.syncWishlistPressedState = syncWishlistPressedState;

  // ====================================================================
  // Cart status announcements (screen reader)
  // ====================================================================
  //
  //   Three-step flow on +/- / remove:
  //     1. Action started  ("Adding {product} to your bag")
  //     2. Loading         ("Updating your bag")
  //     3. Result          ("Item added to your bag")
  //
  //   On tab/window return (auto cart refresh):
  //     "Cart is loading"  →  "Your bag has been updated"
  //
  //   Uses TWO persistent aria-live regions ("double-buffer") so messages
  //   can be repeated reliably across VoiceOver/Chrome with rapid updates.

  var CART_PRODUCT_PLACEHOLDER = '__PRODUCT__';
  var CART_ANNOUNCE_GAP_MS = 1200;
  var CART_FOCUS_RETURN_DELAY_MS = 600;

  var CART_DEFAULT_MESSAGES = {
    announceAdding: 'wait, increasing quantity',
    announceRemovingOne: 'wait, decreasing quantity of __PRODUCT__ by one',
    announceRemoving: 'wait, removing quantity of __PRODUCT__ from your bag',
    announceUpdatingBag: 'wait, updating your bag',
    announceCartLoading: 'Cart is loading',
    announceBagUpdated: 'item quantity increased by one',
    announceItemAdded: 'increased quantity for __PRODUCT__ by one',
    announceItemAddedToBag: 'Item added to your bag',
    announceItemUpdated: 'decreased quantity of __PRODUCT__ by one',
    announceItemRemoved: 'removed __PRODUCT__ from your bag',
    requestErrorText: 'There was an error while updating your bag. Please try again.',
  };

  var cartMessages = CART_DEFAULT_MESSAGES;
  var cartPendingAction = null;
  var cartFocusRefreshPending = false;
  var cartLostFocus = false;
  var cartLiveA = null;
  var cartLiveB = null;
  var cartLiveUseA = true;
  var cartQueue = [];
  var cartQueueRunning = false;

  function loadCartMessages() {
    var configEl = document.querySelector('[data-ajax-cart-configuration]');
    if (!configEl) return;
    try {
      cartMessages = Object.assign({}, CART_DEFAULT_MESSAGES, JSON.parse(configEl.textContent));
    } catch (error) {
      cartMessages = CART_DEFAULT_MESSAGES;
    }
  }

  function formatCartMessage(template, productName) {
    if (!template) return '';
    return String(template)
      .split(CART_PRODUCT_PLACEHOLDER)
      .join(productName || '');
  }

  /* ---------- Double-buffered aria-live regions ---------- */

  function createCartLiveRegion() {
    var el = document.createElement('div');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-atomic', 'true');
    el.className = 'visually-hidden';
    return el;
  }

  function ensureCartLiveRegions() {
    if (cartLiveA && cartLiveA.isConnected && cartLiveB && cartLiveB.isConnected) return;
    cartLiveA = createCartLiveRegion();
    cartLiveB = createCartLiveRegion();
    document.body.appendChild(cartLiveA);
    document.body.appendChild(cartLiveB);
  }

  function speakCart(message, assertive) {
    if (!message) return;
    ensureCartLiveRegions();

    var active = cartLiveUseA ? cartLiveA : cartLiveB;
    var inactive = cartLiveUseA ? cartLiveB : cartLiveA;
    var ariaLive = assertive ? 'assertive' : 'polite';

    if (active.getAttribute('aria-live') !== ariaLive) {
      active.setAttribute('aria-live', ariaLive);
    }

    inactive.textContent = '';
    active.textContent = message;
    cartLiveUseA = !cartLiveUseA;
  }

  function enqueueCartAnnounce(message, assertive) {
    if (!message) return;
    cartQueue.push({ message: message, assertive: !!assertive });
    runCartQueue();
  }

  function runCartQueue() {
    if (cartQueueRunning || !cartQueue.length) return;
    cartQueueRunning = true;
    var item = cartQueue.shift();
    speakCart(item.message, item.assertive);
    window.setTimeout(function () {
      cartQueueRunning = false;
      runCartQueue();
    }, CART_ANNOUNCE_GAP_MS);
  }

  /* ---------- Line-item action context ---------- */

  function getLineItemContext(initiator) {
    if (!initiator || typeof initiator.closest !== 'function') return null;

    var lineItem = initiator.closest('.line-item');
    if (!lineItem) return null;

    var productName =
      lineItem.dataset.productTitle ||
      lineItem.querySelector('.line-item__title-link')?.textContent?.trim() ||
      '';

    if (!productName) return null;

    var action = null;
    if (initiator.classList.contains('line-item__qty-button--increase')) action = 'increase';
    else if (initiator.classList.contains('line-item__qty-button--decrease')) action = 'decrease';
    else if (initiator.classList.contains('line-item__remove-link')) action = 'remove';

    return action ? { productName: productName, action: action } : null;
  }

  /* ---------- Event handlers ---------- */

  function onCartControlClick(event) {
    var trigger = event.target.closest('[data-ajax-cart-request-button]');
    if (!trigger) return;

    var ctx = getLineItemContext(trigger);
    if (!ctx) return;

    var actionMessage = '';
    if (ctx.action === 'increase') {
      actionMessage = formatCartMessage(cartMessages.announceAdding, ctx.productName);
    } else if (ctx.action === 'decrease') {
      actionMessage = formatCartMessage(cartMessages.announceRemovingOne, ctx.productName);
    } else if (ctx.action === 'remove') {
      actionMessage = formatCartMessage(cartMessages.announceRemoving, ctx.productName);
    }
    if (!actionMessage) return;

    cartPendingAction = ctx;
    enqueueCartAnnounce(actionMessage);
  }

  function onCartAjaxRequestStart() {
    if (cartPendingAction) {
      enqueueCartAnnounce(cartMessages.announceUpdatingBag);
    }
  }

  function announceCartSuccess(requestState) {
    if (requestState.requestType === 'add') {
      enqueueCartAnnounce(cartMessages.announceItemAddedToBag || cartMessages.announceItemAdded);
      cartPendingAction = null;
      return;
    }

    if (!cartPendingAction) {
      if (
        cartFocusRefreshPending &&
        (requestState.requestType === 'update' || requestState.requestType === 'get')
      ) {
        enqueueCartAnnounce(cartMessages.announceBagUpdated, true);
      }
      return;
    }

    var ctx = cartPendingAction;
    var successMessage = '';
    if (ctx.action === 'remove') {
      successMessage = formatCartMessage(cartMessages.announceItemRemoved, ctx.productName);
    } else if (ctx.action === 'increase') {
      successMessage = formatCartMessage(cartMessages.announceItemAdded, ctx.productName);
    } else if (ctx.action === 'decrease') {
      successMessage = formatCartMessage(cartMessages.announceItemUpdated, ctx.productName);
    }

    if (successMessage) enqueueCartAnnounce(successMessage);
    cartPendingAction = null;
  }

  function resetCartPendingState() {
    cartPendingAction = null;
    cartFocusRefreshPending = false;
  }

  function onCartAjaxRequestEnd(requestState) {
    if (requestState.info && requestState.info.cancel) {
      resetCartPendingState();
      return;
    }

    var failed = !requestState.responseData || !requestState.responseData.ok;
    if (failed) {
      if (cartPendingAction || cartFocusRefreshPending) {
        enqueueCartAnnounce(cartMessages.requestErrorText, true);
      }
      resetCartPendingState();
      return;
    }

    announceCartSuccess(requestState);
    cartFocusRefreshPending = false;
  }

  function bindCartAjaxAnnouncements() {
    if (
      !window.liquidAjaxCart ||
      typeof window.liquidAjaxCart.subscribeToCartAjaxRequests !== 'function'
    ) {
      return;
    }

    window.liquidAjaxCart.subscribeToCartAjaxRequests(function (requestState, subscribeToResult) {
      onCartAjaxRequestStart(requestState);
      subscribeToResult(onCartAjaxRequestEnd);
    });
  }

  /* ---------- Tab / window focus return ---------- */

  function shouldAnnounceFocusCartReload() {
    return !!document.querySelector('[data-ajax-cart-section]');
  }

  function markFocusCartRefresh() {
    if (!shouldAnnounceFocusCartReload()) return;

    cartFocusRefreshPending = true;
    // Give VoiceOver/Chrome time to re-attach to the window before the
    // live region update — otherwise the announcement is dropped.
    window.setTimeout(function () {
      if (cartFocusRefreshPending) {
        enqueueCartAnnounce(cartMessages.announceCartLoading, true);
      }
    }, CART_FOCUS_RETURN_DELAY_MS);
  }

  function bindCartFocusReturn() {
    // window.blur/focus catches click into DevTools + Cmd+Tab to another app.
    window.addEventListener('blur', function () {
      cartLostFocus = true;
    });
    window.addEventListener('focus', function () {
      if (!cartLostFocus) return;
      cartLostFocus = false;
      markFocusCartRefresh();
    });

    // Fallback for cases where window.focus doesn't fire (Cmd+Tab to another desktop).
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') {
        cartLostFocus = true;
      } else if (document.visibilityState === 'visible' && cartLostFocus) {
        cartLostFocus = false;
        markFocusCartRefresh();
      }
    });
  }

  function initCartStatusAnnouncements() {
    loadCartMessages();
    ensureCartLiveRegions();

    document.addEventListener('click', onCartControlClick, true);
    bindCartFocusReturn();

    if (window.liquidAjaxCart) {
      bindCartAjaxAnnouncements();
    } else {
      document.body.addEventListener('liquidAjaxCartInit', bindCartAjaxAnnouncements);
    }
  }

  // ====================================================================
  // Bootstrap
  // ====================================================================

  function initCustomAda() {
    initWishlistAriaLabels();
    initWishlistPressedSync();
    initCartStatusAnnouncements();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCustomAda);
  } else {
    initCustomAda();
  }
})(window);

// (function () {
//   var SLICK_SELECTOR = '.boost-sd__slider-container.slick-initialized';
//   var SWIPER_READY_SELECTOR = '.boost-sd__slider-container[data-boost-swiper-ready="true"]';
//   var maintainTimer = null;
//   var domObserver = null;

//   var SWIPER_CONFIG = {
//     watchSlidesProgress: true,
//     slidesPerView: 4,
//     spaceBetween: 20,
//     speed: 600,
//     a11y: {
//       enabled: true,
//       slideLabelMessage: 'Slide {{index}} of {{slidesLength}}',
//       prevSlideMessage: 'Previous products',
//       nextSlideMessage: 'Next products',
//     },
//     breakpoints: {
//       320: { slidesPerView: 1.2, spaceBetween: 12 },
//       768: { slidesPerView: 2, spaceBetween: 16 },
//       1024: { slidesPerView: 4, spaceBetween: 20 },
//     },
//   };

//   function normalizeId(value) {
//     return value ? String(value).replace(/^boost-sd-widget-/, '') : '';
//   }

//   function getWidgetId(widget) {
//     if (!widget) return '';
//     return (
//       normalizeId(widget.widgetId) ||
//       normalizeId(widget.config && widget.config.widgetId) ||
//       normalizeId(widget.settings && widget.settings.widgetId) ||
//       normalizeId(widget.id) ||
//       normalizeId(widget.__blockKey)
//     );
//   }

//   function getBlocks(integration) {
//     integration = integration || window.boostWidgetIntegration;
//     if (!integration) return {};
//     return integration.blocks || (integration.TAEApp && integration.TAEApp.blocks) || {};
//   }

//   function findWidgetById(widgetId) {
//     var id = normalizeId(widgetId);
//     if (!id) return null;

//     var blocks = getBlocks();
//     var keys = Object.keys(blocks);
//     for (var i = 0; i < keys.length; i++) {
//       var key = keys[i];
//       var widget = blocks[key];
//       if (!widget || widget.blockType !== 'recommendation') continue;
//       widget.__blockKey = key;
//       if (!widget.widgetId) widget.widgetId = normalizeId(key);
//       if (getWidgetId(widget) === id) return widget;
//     }
//     return null;
//   }

//   function getWidgets(integration) {
//     var blocks = getBlocks(integration);
//     var widgets = [];
//     var seen = {};

//     Object.keys(blocks).forEach(function (key) {
//       var widget = blocks[key];
//       if (!widget || widget.blockType !== 'recommendation') return;
//       widget.__blockKey = key;
//       if (!widget.widgetId) widget.widgetId = normalizeId(key);
//       var id = getWidgetId(widget);
//       if (id && seen[id]) return;
//       if (id) seen[id] = true;
//       widgets.push(widget);
//     });

//     if (widgets.length) return widgets;

//     document.querySelectorAll('.boost-sd__recommendation[id]').forEach(function (rec) {
//       var id = rec.id;
//       if (!id || seen[id]) return;
//       seen[id] = true;
//       widgets.push(
//         findWidgetById(id) || {
//           blockType: 'recommendation',
//           widgetId: id,
//           document: document.getElementById('boost-sd-widget-' + id) || rec,
//         },
//       );
//     });

//     return widgets;
//   }

//   function getContainer(widget) {
//     if (!widget) return null;
//     var id = getWidgetId(widget);
//     if (id) {
//       var rec = document.getElementById(id);
//       if (rec) return rec;
//       var mount = document.getElementById('boost-sd-widget-' + id);
//       if (mount) return mount;
//     }
//     var doc = widget.document;
//     return doc && doc.nodeType === 1 && doc !== document ? doc : null;
//   }

//   function collectSlides(slider) {
//     var slides = slider.querySelectorAll('.boost-sd__slide');
//     if (slides.length) return Array.prototype.slice.call(slides);
//     slides = slider.querySelectorAll('.slick-slide:not(.slick-cloned)');
//     return slides.length ? Array.prototype.slice.call(slides) : [];
//   }

//   function findSlider(container) {
//     if (!container) return null;

//     var slick = container.querySelector(SLICK_SELECTOR);
//     if (slick && !slick.dataset.boostSwiperReady) return slick;

//     var candidates = container.querySelectorAll('.boost-sd__slider-container');
//     for (var i = 0; i < candidates.length; i++) {
//       var slider = candidates[i];
//       if (slider.dataset.boostSwiperReady === 'true') continue;
//       if (collectSlides(slider).length) return slider;
//     }
//     return null;
//   }

//   function resolveNavEl(ref) {
//     return typeof ref === 'string' ? document.querySelector(ref) : ref;
//   }

//   function navIsLive(swiper) {
//     if (!swiper || !swiper.params || !swiper.params.navigation) return false;
//     var prev = resolveNavEl(swiper.params.navigation.prevEl);
//     var next = resolveNavEl(swiper.params.navigation.nextEl);
//     return !!(prev && next && prev.isConnected && next.isConnected);
//   }

//   function findNav(slider, widget) {
//     var root = getContainer(widget);
//     if (!root) return { prevEl: null, nextEl: null };

//     var prevEl =
//       root.querySelector('.boost-sd__prev-button .boost-sd__slider-button') ||
//       root.querySelector('.boost-sd__prev-button button');
//     var nextEl =
//       root.querySelector('.boost-sd__next-button .boost-sd__slider-button') ||
//       root.querySelector('.boost-sd__next-button button');

//     if (!prevEl && slider && slider.parentElement) {
//       prevEl =
//         slider.parentElement.querySelector('.boost-sd__prev-button .boost-sd__slider-button') ||
//         slider.parentElement.querySelector('.boost-sd__prev-button button');
//       nextEl =
//         slider.parentElement.querySelector('.boost-sd__next-button .boost-sd__slider-button') ||
//         slider.parentElement.querySelector('.boost-sd__next-button button');
//     }

//     return { prevEl: prevEl, nextEl: nextEl };
//   }

//   function refreshNav(swiper, slider, widget) {
//     var nav = findNav(slider, widget);
//     if (!nav.prevEl || !nav.nextEl || !swiper) return false;

//     swiper.params.navigation = swiper.params.navigation || {};
//     swiper.params.navigation.prevEl = nav.prevEl;
//     swiper.params.navigation.nextEl = nav.nextEl;

//     if (swiper.navigation) {
//       if (typeof swiper.navigation.destroy === 'function') swiper.navigation.destroy();
//       if (typeof swiper.navigation.init === 'function') swiper.navigation.init();
//       if (typeof swiper.navigation.update === 'function') swiper.navigation.update();
//     }
//     return true;
//   }

//   function unslick(slider) {
//     var $el = window.jQuery && window.jQuery(slider);
//     if (!$el || !$el.hasClass('slick-initialized')) return;
//     slider.__boostUnslicking = true;
//     try {
//       $el.slick('unslick');
//     } catch (err) {
//       console.warn('[Boost Swiper] unslick failed', err);
//     }
//     slider.__boostUnslicking = false;
//   }

//   function bindA11y(swiper) {
//     if (window.CustomAdaSwiper && typeof window.CustomAdaSwiper.initAccessibleSwiper === 'function') {
//       window.CustomAdaSwiper.initAccessibleSwiper(swiper, {
//         activeSlideClass: 'swiper-slide-visible',
//       });
//     }
//   }

//   function getHealth(widget) {
//     var container = getContainer(widget);
//     if (!container) return 'pending';
//     if (container.querySelector(SLICK_SELECTOR)) return 'reinit';

//     var swiperEl = container.querySelector(SWIPER_READY_SELECTOR);
//     if (swiperEl && swiperEl.swiper && !swiperEl.swiper.destroyed) {
//       return navIsLive(swiperEl.swiper) ? 'ok' : 'nav-stale';
//     }
//     if (container.querySelector('.boost-sd__slider-container')) return 'convert';
//     return 'pending';
//   }

//   function buildSwiper(slider, slides, widget) {
//     if (typeof window.Swiper !== 'function' || !slides.length) return null;

//     var nav = findNav(slider, widget);
//     if (!nav.prevEl || !nav.nextEl) {
//       console.warn('[Boost Swiper] prev/next buttons not found');
//       return null;
//     }

//     if (slider.swiper && slider.swiper.destroy) slider.swiper.destroy(true, true);
//     if (slider.classList.contains('slick-initialized')) unslick(slider);

//     slider.classList.remove('slick-slider', 'slick-dotted', 'slick-initialized');
//     slider.classList.add('swiper');
//     slider.setAttribute('role', 'region');
//     slider.setAttribute('aria-roledescription', 'carousel');
//     slider.setAttribute('aria-label', 'Recommended products');

//     var wrapper = document.createElement('div');
//     wrapper.className = 'swiper-wrapper';
//     slides.forEach(function (slide) {
//       slide.classList.remove('slick-slide', 'slick-active', 'slick-current', 'slick-cloned');
//       slide.removeAttribute('style');
//       slide.classList.add('swiper-slide');
//       wrapper.appendChild(slide);
//     });

//     slider.innerHTML = '';
//     slider.appendChild(wrapper);

//     var swiper = new window.Swiper(slider, {
//       watchSlidesProgress: SWIPER_CONFIG.watchSlidesProgress,
//       slidesPerView: SWIPER_CONFIG.slidesPerView,
//       spaceBetween: SWIPER_CONFIG.spaceBetween,
//       speed: SWIPER_CONFIG.speed,
//       a11y: SWIPER_CONFIG.a11y,
//       navigation: { prevEl: nav.prevEl, nextEl: nav.nextEl },
//       breakpoints: SWIPER_CONFIG.breakpoints,
//     });

//     slider.dataset.boostSwiperReady = 'true';
//     slider.dataset.boostSwiperWidget = getWidgetId(widget);
//     widget.__boostSlider = slider;
//     widget.__boostConverted = true;
//     bindA11y(swiper);
//     return swiper;
//   }

//   function initSwiper(widget, slider) {
//     if (!widget || !slider || slider.__boostConverting) return;
//     if (slider.dataset.boostSwiperReady === 'true' && slider.swiper && !slider.swiper.destroyed) {
//       refreshNav(slider.swiper, slider, widget);
//       bindA11y(slider.swiper);
//       return;
//     }

//     slider.__boostConverting = true;
//     var slides = collectSlides(slider);
//     unslick(slider);

//     function finish() {
//       if (typeof window.Swiper !== 'function') {
//         slider.__boostConverting = false;
//         return;
//       }
//       try {
//         buildSwiper(slider, slides.length ? slides : collectSlides(slider), widget);
//       } catch (err) {
//         console.error('[Boost Swiper] init failed', err);
//       }
//       slider.__boostConverting = false;
//     }

//     if (typeof window.Swiper === 'function') {
//       requestAnimationFrame(finish);
//     } else {
//       var n = 0;
//       var t = setInterval(function () {
//         n++;
//         if (typeof window.Swiper === 'function' || n > 50) {
//           clearInterval(t);
//           finish();
//         }
//       }, 100);
//     }
//   }

//   function convertWidget(widget) {
//     if (!widget || widget.blockType !== 'recommendation') return;
//     var container = getContainer(widget);
//     var slider = findSlider(container);
//     if (slider) initSwiper(widget, slider);
//   }

//   function maintainWidget(widget) {
//     if (!widget || widget.blockType !== 'recommendation') return;

//     var tracked = widget.__boostSlider;
//     if (tracked && (!tracked.isConnected || !tracked.swiper || tracked.swiper.destroyed)) {
//       widget.__boostSlider = null;
//     }

//     var health = getHealth(widget);
//     if (health === 'reinit' || health === 'convert') {
//       var container = getContainer(widget);
//       var stale = container && container.querySelector(SWIPER_READY_SELECTOR);
//       if (stale && stale.swiper && !stale.swiper.destroyed) {
//         try {
//           stale.swiper.destroy(true, true);
//         } catch (e) {}
//       }
//       convertWidget(widget);
//       return;
//     }

//     if (health === 'ok' || health === 'nav-stale') {
//       var el = widget.__boostSlider || getContainer(widget).querySelector(SWIPER_READY_SELECTOR);
//       if (el && el.swiper) {
//         widget.__boostSlider = el;
//         refreshNav(el.swiper, el, widget);
//         el.swiper.update();
//         bindA11y(el.swiper);
//       }
//     }
//   }

//   function scheduleMaintain(urgent) {
//     if (urgent) {
//       if (maintainTimer) clearTimeout(maintainTimer);
//       maintainTimer = null;
//       getWidgets().forEach(maintainWidget);
//       return;
//     }
//     if (maintainTimer) clearTimeout(maintainTimer);
//     maintainTimer = setTimeout(function () {
//       maintainTimer = null;
//       getWidgets().forEach(maintainWidget);
//     }, 50);
//   }

//   function hookSlickGuard() {
//     if (window.__boostSlickGuard || !window.jQuery || !window.jQuery.fn.slick) return;
//     window.__boostSlickGuard = true;
//     var original = window.jQuery.fn.slick;

//     window.jQuery.fn.slick = function guardedSlick() {
//       var result = original.apply(this, arguments);
//       if (typeof arguments[0] === 'string' && arguments[0] === 'unslick') return result;

//       this.each(function () {
//         var el = this;
//         if (!el.classList || !el.classList.contains('boost-sd__slider-container')) return;
//         if (el.__boostConverting || el.__boostUnslicking) return;
//         var rec = el.closest('.boost-sd__recommendation[id]');
//         if (!rec) return;
//         var widget = findWidgetById(rec.id);
//         if (widget && (widget.__boostConverted || el.dataset.boostSwiperReady === 'true')) {
//           scheduleMaintain(true);
//         }
//       });
//       return result;
//     };
//   }

//   function ensureDomObserver() {
//     if (domObserver) return;
//     domObserver = new MutationObserver(function () {
//       scheduleMaintain(true);
//     });
//     domObserver.observe(document.body, {
//       childList: true,
//       subtree: true,
//       attributes: true,
//       attributeFilter: ['class'],
//     });
//   }

//   function bindWidget(widget) {
//     if (!widget || widget.blockType !== 'recommendation') return;

//     if (!widget.__boostRenderHooked && typeof widget.render === 'function') {
//       widget.__boostRenderHooked = true;
//       var original = widget.render.bind(widget);
//       widget.render = function (cb) {
//         return original(function () {
//           if (typeof cb === 'function') cb();
//           scheduleMaintain(true);
//         });
//       };
//     }
//     maintainWidget(widget);
//   }

//   function registerBoost(integration) {
//     if (!integration || typeof integration.regisCustomization !== 'function') return;
//     if (integration.__boostSwiperReady) return;
//     integration.__boostSwiperReady = true;
//     integration.regisCustomization(function afterRender() {
//       scheduleMaintain(true);
//     }, 'recommendation');
//   }

//   function isBoostReady(integration) {
//     return (
//       integration &&
//       integration.status === 'ready' &&
//       (Object.keys(getBlocks(integration)).length ||
//         document.querySelector('.boost-sd__recommendation[id]'))
//     );
//   }

//   function bootstrap() {
//     var integration = window.boostWidgetIntegration;
//     if (!integration) return false;
//     registerBoost(integration);
//     if (!isBoostReady(integration)) return false;
//     getWidgets(integration).forEach(bindWidget);
//     ensureDomObserver();
//     hookSlickGuard();
//     scheduleMaintain(true);
//     return true;
//   }

//   registerBoost(window.boostWidgetIntegration);
//   hookSlickGuard();

//   var wait = setInterval(function () {
//     if (bootstrap()) clearInterval(wait);
//   }, 100);
//   setTimeout(function () {
//     clearInterval(wait);
//   }, 30000);

//   window.addEventListener('boost:recommendation:loaded', function () {
//     var integration = window.boostWidgetIntegration;
//     registerBoost(integration);
//     if (isBoostReady(integration)) {
//       getWidgets(integration).forEach(bindWidget);
//       scheduleMaintain(true);
//     }
//   });
// })();
